import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { qty, useTenantCatalog } from '../hooks';
import type { Page, UsageEvent } from '../types';

/** Поток потребления: что пришло, по какому счётчику и от кого. Экран для разбора спорных счетов. */
export function UsagePage() {
  const { tenantId = '' } = useParams();
  const catalog = useTenantCatalog(tenantId);
  const [customerId, setCustomerId] = useState('');

  const events = useQuery({
    queryKey: ['usage', tenantId, customerId],
    queryFn: () =>
      api.get<Page<UsageEvent>>(
        `/tenants/${tenantId}/usage-events?size=100&sort=occurredAt,desc` +
          (customerId ? `&customerId=${customerId}` : ''),
      ),
  });

  const rows = events.data?.content ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Потребление</h1>
          <div className="muted small">{events.data ? `Событий: ${events.data.totalElements}` : ' '}</div>
        </div>
        <div className="spacer" />
      </div>

      <div className="filters">
        <select
          className="input"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          aria-label="Абонент"
        >
          <option value="">Все абоненты</option>
          {catalog.customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {events.isLoading ? (
        <div className="state">Загрузка…</div>
      ) : rows.length === 0 ? (
        <div className="card">
          <div className="state-title">Событий пока нет</div>
          <p className="muted">
            События приходят по API с идентификатором отправителя: повторная отправка того же
            события ничего не меняет, поэтому источники могут спокойно повторять запросы.
          </p>
        </div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Счётчик</th>
                <th>Абонент</th>
                <th className="num">Количество</th>
                <th>Идентификатор</th>
                <th>Свойства</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((event) => (
                <tr key={event.id}>
                  <td className="muted small">{new Date(event.occurredAt).toLocaleString('ru-RU')}</td>
                  <td>{catalog.meterById.get(event.meterId)?.name ?? '—'}</td>
                  <td>{catalog.customerById.get(event.customerId)?.name ?? '—'}</td>
                  <td className="num">{qty(event.quantity)}</td>
                  <td className="muted small">
                    <code>{event.externalId}</code>
                  </td>
                  <td className="muted small props">{event.properties ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
